import { Coins, Question, Lightbulb, Gear, ArrowRight, Sparkle, Scales, Shield, TrendUp, Handshake } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

function Section({ icon: Icon, title, subtitle, tone, children, testid }) {
  const tones = {
    amber: "border-amber-500/30 bg-amber-500/5",
    emerald: "border-emerald-500/25 bg-emerald-500/5",
    blue: "border-blue-500/25 bg-blue-500/5",
  };
  const iconTones = {
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  };
  return (
    <section className={`rounded-sm border ${tones[tone]} p-5 sm:p-6`} data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-sm border flex items-center justify-center shrink-0 ${iconTones[tone]}`}>
          <Icon size={20} weight="bold" />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg sm:text-xl font-bold text-zinc-50">{title}</h2>
          {subtitle && <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">{children}</div>
    </section>
  );
}

function Pill({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
      {Icon && <Icon size={14} weight="bold" className="text-amber-400 mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs font-medium text-zinc-100 mb-0.5">{title}</p>
        <p className="text-[11px] text-zinc-400 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

export default function Money() {
  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1100px] overflow-x-hidden" data-testid="money-page">
      {/* Hero */}
      <div className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-900/30 to-zinc-950 p-5 sm:p-6">
        <div className="flex items-center gap-2.5 mb-2">
          <Coins size={22} weight="bold" className="text-amber-400" />
          <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-500/15 text-amber-300">FOUNDATION</Badge>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">Money</h1>
        <p className="text-xs sm:text-sm text-zinc-400 mt-2 max-w-2xl">
          Before budgets, interest rates, crypto, or stocks — it helps to understand what money actually is. This page breaks
          it down into <span className="text-amber-300 font-medium">What</span>, <span className="text-amber-300 font-medium">Why</span>,
          and <span className="text-amber-300 font-medium">How</span> — in plain English.
        </p>
      </div>

      {/* WHAT */}
      <Section
        icon={Question}
        title="What is money?"
        subtitle="The simplest definition first"
        tone="amber"
        testid="money-what"
      >
        <p>
          Money is a <span className="text-amber-300 font-medium">tool we all agreed to use</span> to trade things. Instead
          of swapping a chicken for a pair of shoes, we use money as a middle step — you sell the chicken for money, then
          use that money to buy shoes.
        </p>
        <p>That's it. Money only works because everyone around you treats it like it has value.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
          <Pill
            icon={Handshake}
            title="A Medium of Exchange"
            body="It lets two strangers trade without needing to want each other's stuff."
          />
          <Pill
            icon={Scales}
            title="A Unit of Measurement"
            body="It gives us a common ruler so we can price a coffee, a car, or a house on the same scale."
          />
          <Pill
            icon={Shield}
            title="A Store of Value"
            body="You can earn today and spend next week — as long as the money holds its worth."
          />
        </div>

        <div className="mt-4 p-3 rounded-sm border border-zinc-800 bg-zinc-950/60">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">Quick Example</p>
          <p className="text-xs text-zinc-300">
            A $20 bill is paper. A bank balance is just a number in a database. A Bitcoin is a line in a public ledger.
            None of these have "value" on their own — we collectively <em>decide</em> they do, and that agreement is what makes money work.
          </p>
        </div>
      </Section>

      {/* WHY */}
      <Section
        icon={Lightbulb}
        title="Why does money matter?"
        subtitle="The problems it solves for everyday life"
        tone="emerald"
        testid="money-why"
      >
        <p>
          Before money existed, people used <span className="text-emerald-300 font-medium">barter</span> — swapping stuff
          directly. That sounds nice until you realize: what if the shoemaker doesn't want your chicken? Money solved
          that problem, and a few more along the way.
        </p>

        <ol className="space-y-2 pl-1">
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">01</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It makes trade easy.</strong> You don't have to find someone who wants exactly what you have — you just need cash.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">02</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It stores your effort.</strong> Your 40-hour work week turns into a paycheck you can spend later — groceries next week, rent next month, a vacation next year.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">03</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It's a scorecard.</strong> Prices tell you what people actually value — a phone costs $800, not because someone said so, but because millions of buyers and sellers agreed on it.</span>
          </li>
          <li className="flex gap-2.5">
            <span className="text-emerald-400 font-data font-bold text-xs mt-0.5">04</span>
            <span className="text-xs sm:text-sm text-zinc-300"><strong className="text-zinc-100">It gives you choice.</strong> Money is freedom — freedom to take time off, pick your next job, help family, buy a home, or retire.</span>
          </li>
        </ol>

        <div className="mt-4 p-3 rounded-sm border border-emerald-500/20 bg-emerald-500/5">
          <p className="text-xs text-zinc-300 leading-relaxed">
            <Sparkle size={12} weight="fill" className="inline text-emerald-400 mr-1" />
            <strong className="text-emerald-300">The big takeaway:</strong> money isn't the goal — it's the tool. You're really
            trading <em>time + effort</em> for <em>money</em>, then trading <em>money</em> for <em>things you want or need</em>.
          </p>
        </div>
      </Section>

      {/* HOW */}
      <Section
        icon={Gear}
        title="How does money actually work?"
        subtitle="Where it comes from, where it goes, and what makes it grow or shrink"
        tone="blue"
        testid="money-how"
      >
        <p>There are really only <span className="text-blue-300 font-medium">three things</span> you can do with money once you have it:</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">1</span>
              <p className="text-sm font-medium text-zinc-100">Spend it</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Trade money for things you need (rent, food, bills) or things you want (coffee, games, vacations).</p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">2</span>
              <p className="text-sm font-medium text-zinc-100">Save it</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Keep it safe for later — an emergency fund, a house down-payment, or a rainy day. Ideally somewhere that pays interest.</p>
          </div>

          <div className="p-4 rounded-sm bg-zinc-900/60 border border-zinc-800/80">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-[10px] font-bold">3</span>
              <p className="text-sm font-medium text-zinc-100">Invest it</p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">Put it to work so it can grow — stocks, bonds, crypto, real estate, or a business. More potential reward, more potential risk.</p>
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm border border-blue-500/20 bg-blue-500/5">
          <div className="flex items-center gap-2 mb-2">
            <TrendUp size={14} weight="bold" className="text-blue-400" />
            <p className="text-xs font-medium text-blue-200">Inflation — the quiet thing that changes everything</p>
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">
            Over time, the <strong className="text-zinc-100">same dollar buys less</strong>. A loaf of bread in 1990 cost about $0.70.
            Today it's $3.50+. That's inflation. Leaving all your money in a checking account means it slowly
            loses buying power every year. Saving and investing exist mainly to outrun inflation.
          </p>
        </div>

        <div className="mt-3 p-4 rounded-sm border border-zinc-800 bg-zinc-950/60">
          <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">The Simple Money Rule</p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="text-amber-300 font-medium">Income − Spending = What's left</span>. If "what's left" is positive and
            you <strong className="text-zinc-100">save or invest</strong> it, your money grows over time. If it's negative, you're
            relying on debt — and debt grows too, just against you.
          </p>
        </div>
      </Section>

      {/* Next steps CTA */}
      <div className="rounded-sm border border-amber-500/25 bg-zinc-900/40 p-5 sm:p-6" data-testid="money-next-steps">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRight size={16} weight="bold" className="text-amber-400" />
          <h3 className="font-heading text-base font-bold text-zinc-100">You've got the basics — what next?</h3>
        </div>
        <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed mb-4">
          Now that money makes sense, these tabs build on it. Each one takes about 5–10 minutes to skim.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Pill icon={Coins} title="Budgeting" body="See where your money goes each month — and plan next month." />
          <Pill icon={TrendUp} title="Compound Interest" body="How money grows on top of itself when invested over time." />
          <Pill icon={Shield} title="HYSA" body="Where to park savings so inflation doesn't eat them." />
        </div>
      </div>
    </div>
  );
}
